import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, BookOpen, RotateCw } from "lucide-react";
import { api } from "../lib/api";
import TrafficLight from "../components/shared/TrafficLight";
import ArticleBody from "../components/shared/ArticleBody";
import ArticleCTA from "../components/shared/ArticleCTA";
import SEOHead from "../components/shared/SEOHead";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function CareNewsPost() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");
  const [nonce, setNonce] = useState(0);

  const fetchItem = useCallback((signal) => {
    setStatus("loading");
    setItem(null);
    return api
      .get(`/api/news/${slug}`, { signal })
      .then((r) => {
        setItem(r.data);
        setStatus("ok");
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        if (err?.response?.status === 404) {
          setStatus("not-found");
        } else {
          setStatus("error");
        }
      });
  }, [slug]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchItem(ctrl.signal);
    api.get(`/api/blog`, { signal: ctrl.signal })
      .then((r) => setRelated((r.data.posts || []).slice(0, 2)))
      .catch(() => {});
    return () => ctrl.abort();
  }, [fetchItem, nonce]);

  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setNonce((n) => n + 1);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  if (status === "not-found") {
    return (
      <div className="container-jcc py-24 text-center">
        <SEOHead title="Update not found" path={`/care-news/${slug}`} />
        <h1 className="font-display text-3xl font-bold">Update not found</h1>
        <p className="mt-3 text-ink-muted">This Care News update may have been moved or unpublished.</p>
        <Link to="/care-news" className="btn-outline mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Back to Care News
        </Link>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="container-jcc py-24 max-w-xl text-center">
        <SEOHead title="Couldn't load update" path={`/care-news/${slug}`} />
        <h1 className="font-display text-3xl font-bold">We couldn't load this update</h1>
        <p className="mt-3 text-ink-muted">
          Your connection may have dropped. Please try again.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <button
            type="button"
            onClick={() => setNonce((n) => n + 1)}
            className="btn-teal inline-flex"
            data-testid="care-news-post-retry"
          >
            <RotateCw className="w-4 h-4" /> Try again
          </button>
          <Link to="/care-news" className="btn-outline inline-flex">
            <ArrowLeft className="w-4 h-4" /> Back to Care News
          </Link>
        </div>
      </div>
    );
  }

  if (status === "loading" || !item) {
    return (
      <div className="container-jcc py-24 text-ink-muted text-center" data-testid="care-news-post-loading">
        Loading…
      </div>
    );
  }

  const metaDescription = item.summary || (item.body ? String(item.body).slice(0, 158).replace(/\s+\S*$/, "") + "…" : "");

  return (
    <article className="bg-white" data-testid="page-care-news-post">
      <SEOHead
        title={item.title}
        description={metaDescription}
        path={`/care-news/${item.slug}`}
        type="article"
        image={item.featured_image}
      />

      <div className="container-jcc pt-8 md:pt-12 max-w-3xl">
        <Link to="/care-news" className="btn-outline !px-4 !py-2 text-sm" data-testid="back-to-care-news">
          <ArrowLeft className="w-4 h-4" /> Back to Care News
        </Link>
      </div>

      <header className="container-jcc max-w-3xl pt-6 pb-2">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="pill bg-teal-50 text-teal">{item.category}</span>
          {item.published_at ? (
            <span className="text-sm text-ink-soft flex items-center gap-1">
              <Calendar className="w-4 h-4" /> {formatDate(item.published_at)}
            </span>
          ) : null}
          {item.traffic_light ? <TrafficLight level={item.traffic_light} size="md" /> : null}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.1]">{item.title}</h1>
        {item.summary && (
          <p className="mt-4 text-lg text-ink-muted leading-relaxed">{item.summary}</p>
        )}
        {item.source && (
          <p className="mt-3 text-sm text-ink-soft">Source: {item.source}</p>
        )}
      </header>

      <div className="container-jcc max-w-4xl mt-8">
        <div className="aspect-[16/7] rounded-3xl bg-gradient-to-br from-teal to-purple overflow-hidden">
          {item.featured_image ? (
            <img src={item.featured_image} alt="" className="w-full h-full object-cover" />
          ) : null}
        </div>
      </div>

      <div className="container-jcc max-w-3xl mt-10 text-ink">
        <ArticleBody body={item.body} testId="care-news-body" />
      </div>

      {/* Bottom return-to-index button — visible on desktop & mobile so readers
          always have a clear way back to the Care News hub from the article. */}
      <div className="container-jcc max-w-3xl mt-10 flex justify-center md:justify-start">
        <Link
          to="/care-news"
          className="btn-outline"
          data-testid="back-to-care-news-bottom"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all Care News updates
        </Link>
      </div>

      <div className="container-jcc max-w-4xl">
        <ArticleCTA />
      </div>

      {related.length > 0 && (
        <section className="container-jcc max-w-4xl mt-16 mb-24" data-testid="care-news-related">
          <div className="rounded-2xl border border-slate-200 bg-surface p-6 md:p-8">
            <div className="overline text-teal mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Related guidance from the WIM Blog
            </div>
            <ul className="space-y-3">
              {related.map((p) => (
                <li key={p.slug}>
                  <Link to={`/wim/${p.slug}`} className="flex items-center justify-between gap-4 group">
                    <span className="font-medium text-ink group-hover:text-teal transition-colors">{p.title}</span>
                    <ArrowRight className="w-4 h-4 text-teal shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {!related.length && <div className="pb-24" />}
    </article>
  );
}
